import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ScanLine, CheckCircle2, AlertTriangle, Clock, RefreshCw, ShieldAlert } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Attendance() {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [today, setToday] = useState([]);

  const fetchToday = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/attendance/today`);
      setToday(res.data || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  const handleCheckIn = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/attendance/checkin`, { query: query.trim(), method: "Manual" });
      setResult(res.data);
      setQuery("");
      fetchToday();
    } catch (err) {
      setError(err.response?.data?.detail || "Check-in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div>
        <h1 className="text-3xl md:text-4xl font-serif text-text_primary mb-1">Attendance</h1>
        <p className="text-text_secondary text-sm">Check members in by Member ID or phone number. Scanner / biometric devices can post to the check-in API too.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Check-in panel */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-card_bg border border-border_soft rounded-xl p-6">
            <h3 className="text-base font-semibold text-text_primary mb-4 flex items-center gap-2"><ScanLine size={18} className="text-gold" /> Check In</h3>
            <form onSubmit={handleCheckIn} className="flex gap-3">
              <input
                data-testid="attendance-query-input"
                autoFocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Member ID (e.g. SGFRR001) or phone"
                className="flex-1 bg-main_bg border border-border_soft rounded-xl p-3.5 text-text_primary text-sm outline-none focus:border-gold/50"
              />
              <button data-testid="attendance-checkin-btn" type="submit" disabled={loading} className="bg-primary text-[#111827] px-6 rounded-xl font-bold text-sm disabled:opacity-50 hover:bg-primary_hover transition-colors">
                {loading ? "..." : "Check In"}
              </button>
            </form>

            {error && <div data-testid="attendance-error" className="mt-4 text-sm text-accent_danger bg-accent_danger/10 border border-accent_danger/20 px-4 py-3 rounded-xl">{error}</div>}

            {result && (
              <div data-testid="attendance-result" className={`mt-4 rounded-xl border p-5 ${result.blocked ? "bg-accent_danger/10 border-accent_danger/30" : "bg-accent_success/10 border-accent_success/30"}`}>
                <div className="flex items-center gap-3 mb-2">
                  {result.blocked ? <ShieldAlert className="text-accent_danger" size={24} /> : <CheckCircle2 className="text-accent_success" size={24} />}
                  <div>
                    <p className="text-lg font-bold text-text_primary">{result.member.name}</p>
                    <p className="text-xs text-text_secondary">{result.member.id} • {result.member.package || "No package"}</p>
                  </div>
                </div>
                {result.warnings?.length > 0 ? (
                  <div className="mt-3 space-y-1.5">
                    {result.warnings.map((w, i) => (
                      <p key={i} className="text-sm text-accent_danger flex items-center gap-2"><AlertTriangle size={14} /> {w}</p>
                    ))}
                    <p className="text-xs text-text_secondary mt-2 italic">Entry recorded. Admin override may be required for blocked members.</p>
                  </div>
                ) : (
                  <p className="text-sm text-accent_success mt-1">Welcome! Checked in successfully.</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Live board */}
        <div className="lg:col-span-7">
          <div className="bg-card_bg border border-border_soft rounded-xl p-6">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-base font-semibold text-text_primary">In the Gym Today <span className="text-gold ml-1">({today.length})</span></h3>
              <button data-testid="attendance-refresh" onClick={fetchToday} className="text-text_secondary hover:text-gold transition-colors"><RefreshCw size={16} /></button>
            </div>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto custom-scrollbar pr-1">
              {today.length === 0 ? (
                <p className="text-sm text-text_secondary/70 italic py-6 text-center">No check-ins yet today.</p>
              ) : today.map((a) => (
                <div key={a.id} data-testid="attendance-row" className="flex items-center justify-between bg-main_bg border border-border_soft/60 rounded-xl px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gold/15 text-gold font-bold flex items-center justify-center text-sm">{(a.member_name || "?").charAt(0).toUpperCase()}</div>
                    <div>
                      <p className="text-sm font-semibold text-text_primary">{a.member_name}</p>
                      <p className="text-[11px] text-text_secondary">{a.member_id} • {a.method}</p>
                    </div>
                  </div>
                  <span className="text-xs text-text_secondary flex items-center gap-1.5"><Clock size={13} /> {a.time}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
